import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Nav, Tab, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { profileApi } from './ProfileApi';
import { useApiLoader } from '../../../hooks/useApiLoader';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import './Profile.scss';

// Validation schema for edit profile form
const profileValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name is required')
    .trim(),
  lastName: Yup.string()
    .required('Last name is required')
    .trim(),
  phone: Yup.string()
    .required('Phone number is required')
    .test('valid-phone', 'Phone number must be 10 digits', (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length === 10;
    }),
});

// Validation schema for change password form
const passwordValidationSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters long')
    .matches(/[a-z]/, 'Password must include at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must include at least one uppercase letter')
    .matches(/[0-9]/, 'Password must include at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must include at least one special character'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match'),
});

const Profile: React.FC = () => {
  const { withLoader } = useApiLoader();
  const [activeTab, setActiveTab] = useState<string>('edit-profile');
  const [loading, setLoading] = useState<boolean>(true);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  // Profile data from API
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profileImage: null as string | null
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await withLoader(
        () => profileApi.getAdminProfile(),
        'Loading profile...'
      );

      if (response.status === 'Success' && response.data) {
        setProfileData({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email || '',
          phone: formatPhoneNumber(response.data.phoneNumber) || '',
          profileImage: response.data.profileUrl || null
        });
      }
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      toast.error(error.response?.data?.message || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Get initials for profile photo
  const getInitials = () => {
    if (!profileData.firstName || !profileData.lastName) return 'A';
    return `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}`.toUpperCase();
  };

  // Handle profile photo upload
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }

      // Store file for later upload
      setAvatarFile(file);

      // Preview image
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData(prev => ({ ...prev, profileImage: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (
    values: { firstName: string; lastName: string; phone: string }
  ) => {
    try {
      // Clean phone number before sending to API (remove formatting)
      const cleanedPhone = values.phone.replace(/\D/g, '');

      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: cleanedPhone
      };

      let response;

      // If avatar file is selected, use multipart upload
      if (avatarFile) {
        response = await withLoader(
          () => profileApi.updateAdminProfileWithAvatar(updateData, avatarFile),
          'Updating profile...'
        );
        setAvatarFile(null); // Clear avatar file after upload
      } else {
        // Otherwise, just update profile data
        response = await withLoader(
          () => profileApi.updateAdminProfile(updateData),
          'Updating profile...'
        );
      }

      if (response.status === 'Success') {
        toast.success(response.message || 'Profile updated successfully!');
        // Refresh profile data
        await fetchProfileData();
        // Dispatch custom event to notify header to refresh
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    }
  };

  // Handle password change
  const handlePasswordChange = async (
    values: typeof passwordData,
    { resetForm, setFieldError }: FormikHelpers<typeof passwordData>
  ) => {
    try {
      const response = await withLoader(
        () => profileApi.changeAdminPassword(values),
        'Changing password...'
      );

      if (response.status === 'Success') {
        toast.success(response.message || 'Password changed successfully!');
        resetForm();
      } else {
        // Always show error under the current password field for failed password changes
        const errorMessage = response.message || 'Failed to change password';
        setFieldError('currentPassword', errorMessage);
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      // Show error under the current password field instead of toast
      const errorMessage = error.response?.data?.message || 'Failed to change password';
      setFieldError('currentPassword', errorMessage);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Handle phone number formatting as user types
  const handlePhoneChange = (e: any, setFieldValue: (field: string, value: any) => void) => {
    const input = e.target.value;
    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);

    // Format as (XXX)-XXX XXXX
    let formatted = limited;
    if (limited.length > 6) {
      formatted = `(${limited.slice(0, 3)})-${limited.slice(3, 6)} ${limited.slice(6)}`;
    } else if (limited.length > 3) {
      formatted = `(${limited.slice(0, 3)})-${limited.slice(3)}`;
    } else if (limited.length > 0) {
      formatted = `(${limited}`;
    }

    setFieldValue('phone', formatted);
  };

  if (loading) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner animation="border" variant="primary" />
            <span className="ms-3">Loading profile...</span>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className='main-body'>
      <Container fluid className="p-0">
        <div className="profile-page">
          <Row>
            {/* LEFT SIDE - Profile Card */}
            <Col xs={12} sm={12} md={5} lg={4}>
              <Card className="profile-card">
                <Card.Body className="text-center">
                  {/* Profile Photo */}
                  <div className="profile-photo-wrapper mb-4">
                    <div className="profile-photo-container position-relative d-inline-block">
                      <input
                        type="file"
                        id="profile-photo-upload"
                        className="d-none"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                      <label htmlFor="profile-photo-upload" className="cursor-pointer">
                        {profileData.profileImage ? (
                          <img
                            src={profileData.profileImage}
                            alt="Profile"
                            className="profile-photo"
                          />
                        ) : (
                          <div className="profile-initials">
                            {getInitials()}
                          </div>
                        )}
                        <div className="camera-icon">
                          <CameraAltOutlinedIcon fontSize="small" />
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Profile Name */}
                  <h3 className="profile-name mb-4" style={{fontSize:'20px'}}>
                    {profileData.firstName} {profileData.lastName}
                  </h3>

                  <hr/>

                  {/* Contact Info */}
                  <div className="profile-contact-info">
                    <div className="contact-item mb-3">
                      <div className="contact-icon">
                        <EmailOutlinedIcon />
                      </div>
                      <div className="contact-details">
                        <div className="contact-label">Email</div>
                        <div className="contact-value">{profileData.email}</div>
                      </div>
                    </div>

                    <div className="contact-item">
                      <div className="contact-icon">
                        <PhoneOutlinedIcon />
                      </div>
                      <div className="contact-details">
                        <div className="contact-label">Phone</div>
                        <div className="contact-value">{profileData.phone}</div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* RIGHT SIDE - Tabbed Content */}
            <Col xs={12} sm={12} md={7} lg={8}>
              <Card className="profile-tabs-card">
                <Card.Body>
                  <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
                    {/* Tab Navigation */}
                    <Nav variant="pills" className="mb-0">
                      <Nav.Item>
                        <Nav.Link
                          active={activeTab === 'edit-profile'}
                          onClick={() => setActiveTab('edit-profile')}
                        >
                          Edit Profile
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          active={activeTab === 'change-password'}
                          onClick={() => setActiveTab('change-password')}
                        >
                          Change Password
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>

                    {/* Tab Content */}
                    <div className="tab-content">
                      {/* Edit Profile Tab */}
                      <div className={`tab-pane ${activeTab === 'edit-profile' ? 'active show' : ''}`}>
                        <Formik
                          initialValues={{
                            firstName: profileData.firstName,
                            lastName: profileData.lastName,
                            phone: profileData.phone
                          }}
                          validationSchema={profileValidationSchema}
                          onSubmit={handleProfileUpdate}
                          enableReinitialize
                          validateOnChange={true}
                          validateOnBlur={true}
                        >
                          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, isValid, dirty, setFieldValue }) => (
                            <Form onSubmit={handleSubmit}>
                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="firstName"
                                      value={values.firstName}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Jane"
                                      isInvalid={touched.firstName && !!errors.firstName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.firstName}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="lastName"
                                      value={values.lastName}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Cooper"
                                      isInvalid={touched.lastName && !!errors.lastName}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.lastName}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                              </Row>

                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                      type="email"
                                      value={profileData.email}
                                      placeholder="janecooper@email.com"
                                      disabled
                                      className="bg-light"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                      type="tel"
                                      name="phone"
                                      value={values.phone}
                                      onChange={(e) => handlePhoneChange(e, setFieldValue)}
                                      onBlur={handleBlur}
                                      placeholder="(XXX)-XXX XXXX"
                                      isInvalid={touched.phone && !!errors.phone}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errors.phone}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                              </Row>

                              <div className="text-start mt-4">
                                <Button type="submit" className="update-btn" disabled={!isValid || (!dirty && !avatarFile) || isSubmitting}>
                                  Update
                                </Button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>

                      {/* Change Password Tab */}
                      <div className={`tab-pane ${activeTab === 'change-password' ? 'active show' : ''}`}>
                        <Formik
                          initialValues={passwordData}
                          validationSchema={passwordValidationSchema}
                          onSubmit={handlePasswordChange}
                          enableReinitialize
                          validateOnChange={true}
                          validateOnBlur={true}
                        >
                          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, isValid, dirty }) => (
                            <Form onSubmit={handleSubmit}>
                              <Form.Group className="mb-3">
                                <Form.Label>Current Password</Form.Label>
                                <div className="password-input-wrapper position-relative">
                                  <Form.Control
                                    type={showPasswords.current ? "text" : "password"}
                                    name="currentPassword"
                                    value={values.currentPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter current password"
                                    isInvalid={touched.currentPassword && !!errors.currentPassword}
                                  />
                                  <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => togglePasswordVisibility('current')}
                                  >
                                    {showPasswords.current ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                  </button>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.currentPassword}
                                  </Form.Control.Feedback>
                                </div>
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>New Password</Form.Label>
                                <div className="password-input-wrapper position-relative">
                                  <Form.Control
                                    type={showPasswords.new ? "text" : "password"}
                                    name="newPassword"
                                    value={values.newPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="* * * * * * * *"
                                    isInvalid={touched.newPassword && !!errors.newPassword}
                                  />
                                  <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => togglePasswordVisibility('new')}
                                  >
                                    {showPasswords.new ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                  </button>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.newPassword}
                                  </Form.Control.Feedback>
                                </div>
                              </Form.Group>

                              <Form.Group className="mb-3">
                                <Form.Label>Confirm Password</Form.Label>
                                <div className="password-input-wrapper position-relative">
                                  <Form.Control
                                    type={showPasswords.confirm ? "text" : "password"}
                                    name="confirmPassword"
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="* * * * * * * *"
                                    isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                                  />
                                  <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                  >
                                    {showPasswords.confirm ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                  </button>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.confirmPassword}
                                  </Form.Control.Feedback>
                                </div>
                              </Form.Group>

                              <div className="text-start mt-4">
                                <Button type="submit" className="update-btn" disabled={!isValid || !dirty || isSubmitting}>
                                  Update
                                </Button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </div>
                  </Tab.Container>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </section>
  );
};

export default Profile;
